import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { Navbar } from "../../../../shared/components/navbar/navbar";
import { Card } from "../../../../shared/components/card/card/card";
import { CardHeader } from "../../../../shared/components/card/card-header/card-header";
import { TableAnnouncements } from '../../../../shared/tables/table-announcements/table-announcements';
import { AnnouncementsService } from '../../../../services/announcements.service';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators, type FormGroup } from '@angular/forms';
import dayjs from 'dayjs';
import type { Announcement, AnnouncementReadPayload } from '../../../../interfaces/announcement';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { EditorModule } from 'primeng/editor';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';
import type { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-announcements-list',
  imports: [
    CommonModule,
    Navbar,
    Card,
    CardHeader,
    TableAnnouncements,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    DividerModule,
    EditorModule
  ],
  templateUrl: './announcements-list.html',
  styleUrl: './announcements-list.css',
})
export class AnnouncementsList {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _announcementsService: AnnouncementsService = inject(AnnouncementsService);

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public announcementDialog: boolean = false;
  public announcementDialogTitle: WritableSignal<string> = signal<string>('');

  public announcementForm: FormGroup = this._fb.group({
    id: [null],
    message: [null],
    read_at: [null],
    read_by: [null],
    status: [null],
    subject: [null],
    answer: [null],
    created_at: [null],
    attachments: [[]],
    notes: [null]
  });

  get readAt(): string {
    return this.announcementForm.get('read_at')?.value;
  }

  public announcementFiltersForm: FormGroup = this._fb.group({
    search_term: [null],
    date_range: [[dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()]],
  });

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  public fromPaymentFiltersForm(key: string): string {

    if (key === 'start_date') {
      return dayjs(this.announcementFiltersForm.get('date_range')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date') {
      return dayjs(this.announcementFiltersForm.get('date_range')?.value[1]).format('YYYY-MM-DD');
    }

    return this.announcementFiltersForm.get(key)?.value;
  }


  private _resetAnnouncementForm(): void {
    this.announcementForm.reset();
    this.announcementForm.patchValue({ attachments: [] });
  }

  public closeDialog(): void {
    this.announcementDialog = false;
    this._resetAnnouncementForm();
  }

  public openDialog(announcement: Announcement): void {
    this._resetAnnouncementForm();
    this.announcementDialog = true;

    this.announcementForm.patchValue({
      ...announcement,
      read_at: announcement.read_at ? dayjs(announcement.read_at).toDate() : null,
    });

    if (announcement.read_at) {
      this.announcementDialogTitle.set('Visualizar comunicado');
      this.announcementForm.disable();
    } else {
      this.announcementDialogTitle.set('Confirmar leitura');
    }
  }

  public submitDialog(): void {
    if (!this.announcementForm.valid) {
      this.announcementForm.markAllAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Preencha todos os campos obrigatórios',
        life: 3000
      });

      return;
    }

    this._readAnnouncement(
      this.announcementForm.get('id')?.value,
      { answer: this.announcementForm.get('answer')?.value }
    );
  }

  private _readAnnouncement(id: string, readPayload: AnnouncementReadPayload): void {
    this._toggleLoading();

    this._announcementsService.read(id, readPayload)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });

            this.closeDialog();
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao tentar ler comunicado',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public openFile(url: string): void {
    window.open(url, '_blank');
  }
}
